import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail(null);

    try {
      await axios.post(`${API}/auth/login`, { email, password }, {
        withCredentials: true
      });
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns structured object when email not verified.
      if (error.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'email_not_verified') {
        setUnverifiedEmail(detail.email || email);
        toast.error(detail.message || 'Please verify your email first.');
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      const r = await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      toast.success(r.data?.message || 'Verification email sent — please check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend verification email. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };
  
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
      <Card className="w-full max-w-md p-8 shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            Welcome Back
          </h1>
          <p className="text-muted-foreground">Sign in to your CompCreator account</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-input"
              required
              className="input-custom mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="password-input"
              required
              className="input-custom mt-1"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full btn-primary"
            data-testid="login-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="text-center">
            <Link to="/forgot-password" className="text-sm font-medium" style={{ color: '#2D4A3E' }} data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          {unverifiedEmail && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-3"
              data-testid="unverified-banner"
            >
              <p>
                Your email <strong>{unverifiedEmail}</strong> hasn&apos;t been
                verified yet. Please check your inbox for the verification
                link, or request a new one:
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleResendVerification}
                disabled={resending}
                data-testid="resend-verification-btn"
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </Button>
            </div>
          )}
        </form>
        
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-stone-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-muted-foreground">Or continue with</span>
          </div>
        </div>
        
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleLogin}
          data-testid="google-login-button"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </Button>
        
        <p className="text-center text-sm text-muted-foreground mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="font-medium" style={{ color: '#2D4A3E' }}>
            Sign up
          </Link>
        </p>
      </Card>
    </div>
  );
}
